// Date / time formatting helpers. Browser-native Intl APIs only —
// no moment, no date-fns. Handles only the formats the dashboard needs.

const HEADER_DATE_FMT = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
})

const SHORT_DATE_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
})

const WEEKDAY_FMT = new Intl.DateTimeFormat('en-US', { weekday: 'long' })

export function formatHeaderDate(isoDate: string): string {
  const d = parseLocalDate(isoDate)
  return HEADER_DATE_FMT.format(d)
}

export function formatShortDate(isoDate: string): string {
  return SHORT_DATE_FMT.format(parseLocalDate(isoDate))
}

export function formatWeekday(isoDate: string): string {
  return WEEKDAY_FMT.format(parseLocalDate(isoDate))
}

// "no check-in yet" if recently active, "last seen Tuesday" within 7
// days, "last seen Apr 18" otherwise.
export function formatLastSeen(
  lastSeenIsoDate: string | null,
  now: Date = new Date()
): string {
  if (!lastSeenIsoDate) return 'no check-in yet'

  const last = parseLocalDate(lastSeenIsoDate)
  const days = daysBetween(last, now)

  if (days <= 1) return 'no check-in yet'
  if (days <= 7) return `last seen ${formatWeekday(lastSeenIsoDate)}`
  return `last seen ${formatShortDate(lastSeenIsoDate)}`
}

// "9:00 AM" — used inside the hours bar tooltips.
const TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
})

export function formatTime(hhmm: string): string {
  // hhmm = "09:00" or "9:00"
  const [hStr, mStr] = hhmm.split(':')
  const d = new Date()
  d.setHours(Number(hStr), Number(mStr || '0'), 0, 0)
  return TIME_FMT.format(d)
}

function parseLocalDate(isoDate: string): Date {
  // Treat YYYY-MM-DD as a local-tz date (avoids the "off by one" UTC
  // pitfall when ISO date strings are parsed by `new Date(...)`).
  const [y, m, d] = isoDate.split('-').map(Number)
  return new Date(y as number, (m as number) - 1, d as number)
}

function daysBetween(a: Date, b: Date): number {
  const oneDay = 24 * 60 * 60 * 1000
  const aMid = new Date(a.getFullYear(), a.getMonth(), a.getDate())
  const bMid = new Date(b.getFullYear(), b.getMonth(), b.getDate())
  return Math.round((bMid.getTime() - aMid.getTime()) / oneDay)
}

// ----- Calendar range helpers ---------------------------------------------

export function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function addDays(isoDate: string, n: number): string {
  const d = parseLocalDate(isoDate)
  d.setDate(d.getDate() + n)
  return toIsoDate(d)
}

// Monday-anchored start of the week containing `isoDate`. Sun=0 wraps to 7.
export function startOfWeekMonday(isoDate: string): string {
  const d = parseLocalDate(isoDate)
  const dow = d.getDay() === 0 ? 7 : d.getDay()
  d.setDate(d.getDate() - (dow - 1))
  return toIsoDate(d)
}

// "Apr 27 – May 3, 2026" — used in the calendar date nav.
const MONTH_DAY_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
})
const YEAR_FMT = new Intl.DateTimeFormat('en-US', { year: 'numeric' })

export function formatRangeLabel(fromIso: string, toIso: string): string {
  const from = parseLocalDate(fromIso)
  const to = parseLocalDate(toIso)
  const fromLabel = MONTH_DAY_FMT.format(from)
  const toLabel = MONTH_DAY_FMT.format(to)
  const year = YEAR_FMT.format(to)
  return `${fromLabel} – ${toLabel}, ${year}`
}
